import React, { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { createCustomerPortalSession, cancelSubscription } from '../../utils/stripe/api';
import { SUBSCRIPTION_PLANS } from '../../utils/stripe/config';
import { supabase } from '../../utils/supabase';
import { useAiCreditsStore } from '../../stores/aiCreditsStore';
import { CurrentSubscriptionData } from '../pricing/PricingPlans';

export function PlanDetails() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const user = useAuthStore(state => state.user);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { planCredits, maxPlanCredits, chargedCredits } = useAiCreditsStore();
  const [currentSubscriptionData, setCurrentSubscriptionData] = useState<CurrentSubscriptionData | null>(null);
  
  // Get the current plan from Supabase
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.id) return;
      
      try {
        const { data: userRow } = await supabase
          .from('users')
          .select('plan, next_plan')
          .eq('id', user.id)
          .maybeSingle();

        const { data: stripeCustomer, error: customerError } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (customerError) throw customerError;

        let subscriptionData = null;
        if (stripeCustomer?.customer_id) {
          const { data, error } = await supabase
            .from('stripe_subscriptions')
            .select('*')
            .eq('customer_id', stripeCustomer.customer_id)
            .maybeSingle();

          if (error) throw error;
          subscriptionData = data;
        }
        
        const planFromUser =
          userRow?.plan && userRow.plan !== 'unsubscribed' ? userRow.plan : null;
        const planFromStripe = subscriptionData?.price_id
          ? SUBSCRIPTION_PLANS.find((p) => p.priceId === subscriptionData.price_id)?.id ?? null
          : null;

        if (subscriptionData) {
          setCurrentSubscriptionData(subscriptionData);
          setCurrentPlanId(planFromUser ?? planFromStripe ?? 'unsubscribed');
          setCurrentSubscriptionId(subscriptionData.subscription_id);
          setCancelAtPeriodEnd(subscriptionData.cancel_at_period_end);
          
          if (subscriptionData.current_period_end) {
            const date = new Date(subscriptionData.current_period_end * 1000);
            setNextBillingDate(date.toISOString());
          }
        } else {
          setCurrentPlanId(planFromUser ?? 'unsubscribed');
          setCurrentSubscriptionId(null);
          setCurrentSubscriptionData(null);
        }
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
        setError('プラン情報の取得に失敗しました');
        setCurrentSubscriptionData(null);
        // Default to unsubscribed plan on error
        setCurrentPlanId('unsubscribed');
        setCurrentSubscriptionId(null);
      }
    };
    
    fetchSubscription();
  }, [user?.id]);
  
  const currentPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === currentPlanId) || SUBSCRIPTION_PLANS[0];
  
  const handleManageSubscription = async () => {
    if (!user?.id) return;
    
    // Clear previous messages
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const session = await createCustomerPortalSession(user.id);
      if (session?.url) {
        window.location.assign(session.url);
      }
    } catch (error) {
      console.error('Failed to create portal session:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Handle specific error cases
      if (errorMessage.includes('No Stripe customer found') || 
          errorMessage.includes('customer not found') ||
          errorMessage.includes('Customer not found')) {
        setError('お客様のStripeアカウントが見つかりません。まずプランに登録してください。');
      } else {
        setError(`支払い管理画面の作成に失敗しました: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user?.id || !currentSubscriptionId) return;
    
    // Clear previous messages
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Call the cancel-subscription Edge Function directly
      const result = await cancelSubscription(currentSubscriptionId);
      
      // Update local state to reflect cancellation
      setCancelAtPeriodEnd(true);
      setSuccessMessage('サブスクリプションは次回の請求日をもって解約されます。それまでは引き続きご利用いただけます。');
      
      // Close the modal if it was open
      setShowCancelModal(false);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Handle specific error cases
      if (errorMessage.includes('No Stripe customer found') || 
          errorMessage.includes('customer not found') ||
          errorMessage.includes('Customer not found')) {
        setError('お客様のStripeアカウントが見つかりません。まずプランに登録してください。');
      } else {
        setError(`サブスクリプションの解約処理に失敗しました: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Modal for subscription cancellation confirmation
  const CancelSubscriptionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">サブスクリプションを解約しますか？</h3>
        <div className="bg-yellow-50 p-4 rounded-md mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                解約すると、次回の請求日以降はサービスを利用できなくなります。
                次回の請求日までは引き続き現在のプランをご利用いただけます。
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowCancelModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleCancelSubscription}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-400"
          >
            {isLoading ? '処理中...' : '解約する'}
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-500">現在のプラン</label>
        <p className="mt-1 text-sm text-gray-900">{currentPlan.name}</p>
      </div>

      {/* AI Credits Display */}
      <div>
        <label className="block text-sm font-medium text-gray-500">AIクレジット残高</label>
        <p className="mt-1 text-sm text-gray-900">
          {planCredits} / {maxPlanCredits}
          {chargedCredits > 0 && ` (追加: ${chargedCredits})`}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mt-4 p-3 bg-green-50 text-green-600 text-sm rounded-md flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <p>{successMessage}</p>
        </div>
      )}

      {/* Past Due Warning */}
      {currentSubscriptionData?.status === 'past_due' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
          <p className="text-sm text-red-700">
            <strong>お支払いが滞納されています。</strong> サービスの継続利用にはお支払いが必要です。支払い方法を管理からご確認ください。
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {currentPlanId === 'unsubscribed' ? (
          <button
            onClick={() => navigate('/pricing')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            プランを選択
          </button>
        ) : (
          <>
            <button
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              プランを変更
            </button>
            
            {!cancelAtPeriodEnd && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 hover:border-red-300"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                サブスクリプションを解約
              </button>
            )}
          </>
        )}
        
        <button
          onClick={handleManageSubscription}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          <span>{isLoading ? '読み込み中...' : '支払い方法を管理'}</span>
        </button>
      </div>

      {cancelAtPeriodEnd && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-700">
            <strong>サブスクリプションは解約予定です。</strong> 次回の請求日（{nextBillingDate 
              ? new Date(nextBillingDate).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
              : '未設定'}）以降は自動更新されません。
          </p>
        </div>
      )}
      
      {/* Next Billing Date */}
      <div className="text-xs text-gray-500 pt-2">
        <p>次回の請求日: {nextBillingDate 
          ? new Date(nextBillingDate).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
          : '未設定'}</p>
        {/* <p className="mt-1">
          <a href="#" className="text-blue-600 hover:text-blue-500 inline-flex items-center">
            <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        </p> */}
      </div>

      {/* Cancellation Confirmation Modal */}
      {showCancelModal && <CancelSubscriptionModal />}
    </div>
  );
}